// WHAT MUST HOLD FOR ANY PATH THAT DRAWS SPLATS — checked once, out loud.
//
// THE FAULT THIS EXISTS TO KILL. A capability that must hold for every render
// path kept being wired inside ONE path's React component, so a new path had to
// re-wire every one of them by hand and nothing said so when it did not. Four
// instances, all found in one day, all the same shape:
//
//   - `sortRadial` was set by the avatar's bridge only, and it is a CLASS
//     static, so a GLB character's room sorted on three's planar metric — the
//     +12.8-level wash the radial metric exists to remove.
//   - the stable radix sort was never constructed on the room's path: the
//     component carried the code to DRIVE one and nothing ever built it.
//   - `requestRender()` on new splats is called only by `OGSModel.jsx`, so the
//     next path re-sorted only as a side effect of pacing.
//   - `installLiveBodySkin` lives only in `OGSModel.jsx`, so a live branch on
//     the next path rides one rigid delta on its anchor — the detached head.
//
// THREE of those four were invisible until someone read the right file. The
// fourth was found by a person looking at a screenshot. That is the thing being
// fixed here: not the instances, the invisibility.
//
// WHERE IT IS CHECKED. From shared ground both paths already call — the splat
// mount and the part-drive build — never from a component, because a check that
// a path has to remember to call has the same fault as the capability it checks.
//
// THREE RULES KEEP IT FROM BECOMING NOISE, which is what kills checks like this:
//
//   1. `not-applicable` is a FIRST-CLASS outcome. A GLB character has no
//      branches; that is not a failure and must not print.
//   2. `absent` is an ERROR, once, naming the fix — not a warning. Every item on
//      this list is a VISIBLE DEFECT when missing, so a reader who sees one is
//      looking at a broken frame, not a preference.
//   3. An item may only be added WITH A REPRODUCTION. This list is what has
//      actually broken, not what might.

/** @typedef {'applied'|'not-applicable'|'absent'} ContractState */

/**
 * One requirement. `applies` decides whether the frame is even in scope;
 * `check` returns true, or a string saying what is wrong.
 *
 * @typedef {object} ContractItem
 * @property {string} id
 * @property {(ctx: object) => boolean} applies
 * @property {(ctx: object) => boolean|string} check
 * @property {string} fix  what to do about it, in a sentence a reader can act on
 */

/** WebGPU's own default for a mesh whose splats are lifted on the device. */
const isLiveBranch = (branch, isLive) => (typeof isLive === 'function' ? isLive(branch) : !!branch?.isLive)

/** @type {ContractItem[]} */
export const RENDER_CONTRACT = [
  {
    id: 'splat-class-settings',
    applies: ({ GaussianSplat }) => !!GaussianSplat,
    check: ({ GaussianSplat }) => {
      if (GaussianSplat.kernelCutoff !== 2) return `kernelCutoff is ${GaussianSplat.kernelCutoff}, not 2`
      if (GaussianSplat.sortRadial !== true) return 'sortRadial is not on — the planar metric composites far over near'
      return true
    },
    fix: 'call configureSplatClass (render/three/splatDraw.js) before building a mesh',
  },
  {
    id: 'stable-draw-order',
    applies: ({ mesh }) => !!mesh,
    check: ({ mesh, order }) => {
      if (!order) return 'no SplatOrder drives this mesh — three\'s binned sort re-randomises overlapping splats'
      return true
    },
    fix: 'build a SplatOrder (render/three/splatDraw.js) and drive it per frame',
  },
  {
    id: 'order-follows-lift',
    applies: ({ driver }) => !!driver,
    check: ({ driver }) => (typeof driver.splatsGeneration === 'function'
      ? true
      : 'the renderer cannot tell that a lift happened, so a new pose can be drawn under an older order'),
    fix: 'the driver must expose splatsGeneration() and re-sort when it changes',
  },
  {
    id: 'live-branch-skinned',
    applies: ({ branches, device, isLive }) => !!device && !!branches?.some((b) => isLiveBranch(b, isLive)),
    check: ({ branches, isLive }) => {
      const live = branches.filter((b) => isLiveBranch(b, isLive))
      const anchored = live.filter((b) => !b.bodySkin).map((b) => b.name)
      if (!anchored.length) return true
      return `${anchored.join(', ')} ride a rigid delta on an anchor instead of the body rig`
    },
    fix: 'install the body skinner where the part drive is built (branch/liveBodySkin.js) — '
      + 'a rigid delta cannot be right for a head AND the neck and shoulders below it '
      + '(measured: ~250 mm out at the collar on walk clips)',
  },
]

/**
 * Check every item against one built frame and report.
 *
 * EVERY CALLER SEES ONLY ITS OWN HALF, and that is why `source` is required.
 * The splat mount knows the mesh and the sort and has never heard of a branch;
 * the part-drive build is the exact opposite. Each therefore reports
 * `not-applicable` for the other's items — truthfully, about what IT built — so
 * a report keyed by id alone lets whichever ran last stamp `not-applicable`
 * over the other's `applied`. Measured on myra-pbr: the mount reported three
 * `applied`, the part drive ran after it, and the published contract then read
 * `not-applicable` for all four, with nothing wrong and nothing said. Rows are
 * keyed by `source/id` so the two partial views sit side by side.
 *
 * @param {object} ctx   whatever the caller has built: { branches, device, mesh,
 *   order, driver, GaussianSplat, isLive }
 * @param {object} [opts]
 * @param {object} [opts.log]   defaults to console
 * @param {string} [opts.source]  who is asking, for the report line
 * @returns {Array<{id: string, state: ContractState, why: string|null, fix: string|null}>}
 */
export function checkRenderContract(ctx, { log = console, source } = {}) {
  if (!source) throw new Error('checkRenderContract needs a source: a report that cannot say who built it cannot be read beside another')
  const report = []
  for (const item of RENDER_CONTRACT) {
    let state = 'applied'
    let why = null
    try {
      if (!item.applies(ctx)) state = 'not-applicable'
      else {
        const verdict = item.check(ctx)
        if (verdict !== true) { state = 'absent'; why = typeof verdict === 'string' ? verdict : 'unsatisfied' }
      }
    } catch (e) {
      // A CHECK THAT THROWS IS AN ABSENT CAPABILITY, never a pass: it means the
      // shape it reads is not there, which is the thing it is looking for.
      state = 'absent'
      why = `the check threw: ${e?.message ?? e}`
    }
    report.push({ id: item.id, source, state, why, fix: state === 'absent' ? item.fix : null })
  }
  for (const row of report) {
    if (row.state !== 'absent') continue
    log.error?.(`[render-contract] ${row.source}/${row.id}: ${row.why}\n  fix: ${row.fix}`)
  }
  publish(report)
  return report
}

/** The whole report, for a console session or a spec: every item, including the
 *  ones that did not apply — "not checked" and "checked and fine" are different
 *  facts and a reader needs to tell them apart. Keyed by SOURCE and id, so one
 *  caller's blind spot cannot overwrite what another actually measured. */
function publish(report) {
  if (typeof window === 'undefined') return
  const previous = Array.isArray(window.__RENDER_CONTRACT__) ? window.__RENDER_CONTRACT__ : []
  const rows = new Map(previous.map((row) => [`${row.source}/${row.id}`, row]))
  for (const row of report) rows.set(`${row.source}/${row.id}`, row)
  window.__RENDER_CONTRACT__ = [...rows.values()]
}
